/**
 * React-Query `defaultOptions` for the app's QueryClient.
 *
 * Extracted from main.tsx for the same reason globalErrorHandlers was (see
 * src/lib/globalErrorHandlers.ts): main.tsx bootstraps the whole app and can't
 * be imported in a unit test, so the mutation-reliability rules encoded here
 * were untestable while they lived inline.
 */
import type { DefaultOptions } from '@tanstack/react-query';

/**
 * Query retry policy (2026-09-14, preview/staging single-instance capacity
 * hardening). Was a flat `retry: 1` (retry any failure once, ~1s later).
 *
 * On the preview/staging Cloud Run service (--max-instances 1, cold start
 * ~28s), a burst of page-load requests can hit the instance while it's still
 * cold-starting: Cloud Run's own admission control answers with 429 before
 * the request ever reaches the app, and a genuinely busy single instance can
 * surface 502/503/504 at the gateway layer too. A flat single retry with a
 * fixed ~1s delay often retries BACK INTO the same still-cold window. This
 * policy instead:
 *   - retries ONLY the statuses that mean "the server/gateway couldn't serve
 *     this right now, try again" — 429, 502, 503, 504 — plus a genuine
 *     network failure (fetch() itself rejected: offline, or a response with
 *     no CORS headers, which is exactly what the browser reports as a CORS
 *     error for a request that never got a real answer; see api.ts).
 *   - NEVER retries any other 4xx (400/401/403/404/409/422 etc.) — those are
 *     the server telling us something true and specific; retrying just
 *     delays showing the user the real, actionable error. 401 keeps its own
 *     recovery path (fetchWithAuth's one-shot token-refresh-and-retry,
 *     BEFORE this policy ever sees the response).
 *   - uses exponential backoff WITH jitter, and honours a `Retry-After`
 *     header when the server sent one (api.ts's `parseRetryAfterMs`),
 *     capped so the total retry window stays in the roughly 10-15s range —
 *     long enough to ride out a cold start, short enough that a genuinely
 *     down backend still fails inside one user-perceived "the page is
 *     loading" beat.
 *
 * MUTATIONS are UNCHANGED by this: `retry: 0` / `networkMode: 'always'`
 * below is the 2026-05-30/2026-08-03 Christa-trust decision and this policy
 * does not touch it — a write is never safe to retry blindly.
 *
 * Individual hooks that set their OWN `retry` (useChangeOrders, useDrawAmounts,
 * useInvoices, useOnboardingStatus, usePortalPanelConfig, useSiteVisits, useSOV,
 * ExternalViewPage) are unaffected — a hook-level option always overrides this
 * default, and none of those were touched here.
 */
const RETRYABLE_QUERY_STATUSES = new Set([429, 502, 503, 504]);

/** Maximum retry attempts for a query under the default policy (so the
 * worst case is 4 total attempts: the original + 3 retries). */
const MAX_QUERY_RETRIES = 3;

/** Retries allowed for a request that TIMED OUT rather than failed fast.
 *
 * 2026-09-26: api.ts gives reads a 20s deadline, so a timed-out attempt costs
 * the full 20s instead of returning immediately the way a 502 does. Spending
 * the normal 3-retry budget on those turns this module's documented
 * "roughly 10-15s" window into ~80 seconds of a dead, disabled button.
 *
 * One retry is what the deadline is actually for: a socket that died is
 * re-issued on a fresh connection and succeeds. A second and third retry only
 * lengthen the wait in the case where the endpoint is genuinely down, and in
 * that case the operator is better served by the error arriving. */
const MAX_TIMEOUT_RETRIES = 1;

const QUERY_RETRY_BASE_DELAY_MS = 400;
const QUERY_RETRY_MAX_DELAY_MS = 3000;
/** Upper bound on how long we'll wait even if the server's own Retry-After
 * asks for longer — keeps a single attempt from blowing the whole ~10-15s
 * budget by itself. */
const RETRY_AFTER_CAP_MS = 5000;

interface RetryableErrorShape {
  status?: number;
  retryAfterMs?: number;
  message?: string;
}

/**
 * A signed-out session throws with no `.status` too (getAuthToken throws a
 * plain `Error('No authenticated user')` before fetch ever runs — see
 * api.ts / globalErrorHandlers.ts's identical check). That is NOT a
 * transient network condition a delay can fix, so it must not consume the
 * network-error retry path. Deliberately duplicated here (not imported)
 * rather than pulled in from globalErrorHandlers.ts, to keep this module
 * free of react/toast/firebase-auth-adjacent imports for testability.
 */
function isAuthLikeFailure(error: unknown): boolean {
  const message = (error as RetryableErrorShape)?.message || '';
  return /no authenticated user/i.test(message) || /\bauth\/[a-z-]+/i.test(message);
}

/** True when `error` is worth retrying under the default QUERY policy. */
export function isRetryableQueryError(error: unknown): boolean {
  const status = (error as RetryableErrorShape)?.status;
  if (status === undefined) {
    // No status: fetch() itself rejected (offline / opaque network failure —
    // the exact shape the browser reports as a CORS error for a request
    // that never got a real, header-carrying response) — retryable, UNLESS
    // it's actually a signed-out session in disguise.
    return !isAuthLikeFailure(error);
  }
  return RETRYABLE_QUERY_STATUSES.has(status);
}

/** react-query `retry` option for queries: stop once the error isn't
 * retryable, or once the attempt budget is spent. */
/** True when the request hit our own read deadline (api.ts) rather than
 * failing fast. Matched on DOMException's `name`, which is what
 * AbortSignal-style timeout rejections carry. */
export function isTimeoutFailure(error: unknown): boolean {
  return (error as { name?: string } | undefined)?.name === 'TimeoutError';
}

export function queryRetry(failureCount: number, error: unknown): boolean {
  // A timed-out read already burned the whole deadline, so it gets a SMALLER
  // budget than a fast failure. Without this the worst case is
  // (1 + MAX_QUERY_RETRIES) x the deadline.
  if (isTimeoutFailure(error)) {
    return failureCount <= MAX_TIMEOUT_RETRIES && !isAuthLikeFailure(error);
  }
  if (failureCount > MAX_QUERY_RETRIES) return false;
  return isRetryableQueryError(error);
}

/** react-query `retryDelay` option for queries: honour `Retry-After` when
 * the error carries one (capped), else exponential backoff with full
 * jitter (uniform random in [0, computedDelay]) — spreads out a burst of
 * simultaneous page-load requests instead of having them all retry in
 * lockstep back into the same cold instance. */
export function queryRetryDelay(failureCount: number, error: unknown): number {
  const serverDelay = (error as RetryableErrorShape)?.retryAfterMs;
  if (typeof serverDelay === 'number' && serverDelay > 0) {
    return Math.min(serverDelay, RETRY_AFTER_CAP_MS);
  }
  const exponential = Math.min(
    QUERY_RETRY_BASE_DELAY_MS * 2 ** Math.max(failureCount - 1, 0),
    QUERY_RETRY_MAX_DELAY_MS,
  );
  return Math.random() * exponential;
}

export const queryClientDefaultOptions: DefaultOptions = {
  queries: {
    staleTime: 0, // Always consider data stale - refetch on mount for fresh data
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false, // Disabled - can be annoying during development
    refetchOnMount: true, // CRITICAL: Allow refetch on mount so cache invalidation works
    refetchOnReconnect: true, // Refetch when network reconnects
    retry: queryRetry,
    retryDelay: queryRetryDelay,
    // networkMode deliberately left at the React Query default ("online") for
    // QUERIES: pausing a read while offline is the right behaviour — there is
    // nothing to lose and the data would be unavailable anyway.
  },
  mutations: {
    // 2026-05-30 audit fix: Christa-trust CRITICAL. Was `retry: 1`. A failed
    // mutation that returns 4xx (validation, overpayment, void-conflict) was
    // silently retried — wasted bandwidth at best, but the much scarier case
    // is a transient 5xx / network drop AFTER a successful POST: the DB
    // commits, gateway times out, React Query auto-fires a second POST and
    // creates a duplicate payment. PaymentModal's submittingRef can't catch
    // this because the retry happens at the mutation-cache layer, below the
    // form. Mutations must not auto-retry — they are not idempotent. The
    // user can manually re-submit if the action genuinely failed.
    retry: 0,
    // 2026-08-03 audit fix: same Christa-trust class as `retry: 0` above, from
    // the opposite direction. Unset, React Query v5 defaults mutations to
    // networkMode "online" (query-core retryer.js: `networkMode ?? "online"`).
    // Offline, that makes a mutation PAUSE instead of fail: mutation.js runs
    // `onMutate` (so the optimistic update APPLIES) and only then calls
    // `retryer.start()`, which parks with `isPaused = !canStart()`. onError
    // never fires, the global mutation handler never speaks, and the user sees
    // a screen that looks saved. Close the tab and the write is gone with zero
    // feedback. "always" makes the request actually attempt and fail fast into
    // the existing error path — a real toast and a real optimistic rollback.
    // QUERIES keep the default (see the queries block above).
    networkMode: 'always',
  },
};
